import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Eye, Clock, MousePointer, Globe, Monitor, 
  TrendingUp, LogOut, RefreshCw, Calendar, MapPin,
  Smartphone, Laptop, Tablet, ArrowUpRight, ArrowDownRight,
  Download, Inbox, Mail, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);

  const getToken = () => localStorage.getItem('admin_token');

  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API}/admin/analytics/summary`, {
        params: { days: parseInt(dateRange) },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setAnalytics(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        setError('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/contact/submissions`, {
        params: { page: 1, limit: 500 },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSubmissions(response.data.items || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const exportSubmissionsCsv = () => {
    if (!submissions.length) return;
    const escape = (v) => {
      const s = (v ?? '').toString().replace(/"/g, '""');
      return `"${s}"`;
    };
    const header = ['Name', 'Email', 'Phone', 'Company', 'Industry', 'Annual Revenue', 'Services Expected', 'Vision / Message', 'Locale', 'Status', 'Received At', 'Submission ID'];
    const rows = submissions.map((s) => [
      s.name,
      s.email,
      s.phone,
      s.company,
      s.industry,
      s.business_volume,
      (s.expected_services || []).join(' | '),
      s.goal,
      s.locale,
      s.status,
      s.received_at,
      s.id,
    ].map(escape).join(','));
    const csv = [header.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const ts = new Date().toISOString().slice(0, 10);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `2ds-finland-contact-submissions-${ts}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/admin');
      return;
    }

    // Verify token
    axios.get(`${API}/admin/verify`, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    });

    fetchAnalytics();
    fetchSubmissions();
  }, [dateRange, navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin');
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getDeviceIcon = (device) => {
    if (device?.toLowerCase().includes('mobile')) return <Smartphone className="w-4 h-4" />;
    if (device?.toLowerCase().includes('tablet')) return <Tablet className="w-4 h-4" />;
    return <Laptop className="w-4 h-4" />;
  };

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-ink animate-spin mx-auto mb-4" />
          <p className="text-ink-muted">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment">
      {/* Header */}
      <header className="bg-ink text-parchment py-4 px-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-serif text-xl">2DS Finland Oy</h1>
            <span className="text-parchment/40">|</span>
            <span className="text-parchment/60 text-sm">Analytics Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-parchment/60 text-sm hidden md:block">
              {localStorage.getItem('admin_email')}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-parchment/20 text-parchment hover:bg-parchment/10"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="font-serif text-2xl text-ink mb-1">Visitor Analytics</h2>
            <p className="text-ink-muted text-sm">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-40 bg-white" data-testid="date-range-select">
                <Calendar className="w-4 h-4 mr-2 text-ink-muted" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={fetchAnalytics}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {analytics && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-white border-warm">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-ink-muted text-sm mb-1">Total Visitors</p>
                      <p className="font-serif text-3xl text-ink" data-testid="total-visitors">
                        {analytics.total_visitors.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-accent-indigo/10 rounded-full">
                      <Users className="w-6 h-6 text-accent-indigo" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-warm">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-ink-muted text-sm mb-1">Page Views</p>
                      <p className="font-serif text-3xl text-ink" data-testid="total-pageviews">
                        {analytics.total_page_views.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-accent-teal/10 rounded-full">
                      <Eye className="w-6 h-6 text-accent-teal" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-warm">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-ink-muted text-sm mb-1">Avg. Time on Site</p>
                      <p className="font-serif text-3xl text-ink" data-testid="avg-time">
                        {formatTime(Math.round(analytics.avg_time_on_site))}
                      </p>
                    </div>
                    <div className="p-3 bg-accent-amber/10 rounded-full">
                      <Clock className="w-6 h-6 text-accent-amber" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-warm">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-ink-muted text-sm mb-1">Bounce Rate</p>
                      <p className="font-serif text-3xl text-ink" data-testid="bounce-rate">
                        {analytics.bounce_rate}%
                      </p>
                    </div>
                    <div className="p-3 bg-accent-rose/10 rounded-full">
                      <MousePointer className="w-6 h-6 text-accent-rose" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Visitors Over Time */}
              <Card className="bg-white border-warm">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-accent-teal" />
                    Visitors Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48 flex items-end gap-1">
                    {analytics.visitors_over_time.slice(-14).map((day, i) => {
                      const max = Math.max(...analytics.visitors_over_time.map(d => d.visitors)) || 1;
                      const height = (day.visitors / max) * 100;
                      return (
                        <div
                          key={i}
                          className="flex-1 bg-accent-teal/20 hover:bg-accent-teal/40 transition-colors rounded-t group relative"
                          style={{ height: `${Math.max(height, 4)}%` }}
                        >
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-ink text-parchment text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                            {day.visitors} visitors
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-ink-muted">
                    <span>{analytics.visitors_over_time[analytics.visitors_over_time.length - 14]?.date}</span>
                    <span>{analytics.visitors_over_time[analytics.visitors_over_time.length - 1]?.date}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Traffic Sources */}
              <Card className="bg-white border-warm">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5 text-accent-indigo" />
                    Traffic Sources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3" data-testid="traffic-sources">
                    {Object.entries(analytics.visitors_by_source)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([source, count]) => {
                        const total = Object.values(analytics.visitors_by_source).reduce((a, b) => a + b, 0) || 1;
                        const pct = ((count / total) * 100).toFixed(1);
                        return (
                          <div key={source} className="flex items-center gap-3">
                            <div className="flex-1">
                              <div className="flex justify-between mb-1">
                                <span className="text-sm text-ink">{source}</span>
                                <span className="text-sm text-ink-muted">{count} ({pct}%)</span>
                              </div>
                              <div className="h-2 bg-cream rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-accent-indigo rounded-full"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {Object.keys(analytics.visitors_by_source).length === 0 && (
                      <p className="text-ink-muted text-sm text-center py-4">No data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Geography */}
              <Card className="bg-white border-warm">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-accent-rose" />
                    Visitors by Country
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="visitors-by-country">
                    {Object.entries(analytics.visitors_by_country)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 6)
                      .map(([country, count]) => (
                        <div key={country} className="flex items-center justify-between py-2 border-b border-cream last:border-0">
                          <span className="text-sm text-ink">{country}</span>
                          <span className="text-sm font-medium text-ink">{count}</span>
                        </div>
                      ))}
                    {Object.keys(analytics.visitors_by_country).length === 0 && (
                      <p className="text-ink-muted text-sm text-center py-4">No data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Devices */}
              <Card className="bg-white border-warm">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-accent-amber" />
                    Devices
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="visitors-by-device">
                    {Object.entries(analytics.visitors_by_device)
                      .sort((a, b) => b[1] - a[1])
                      .map(([device, count]) => (
                        <div key={device} className="flex items-center justify-between py-2 border-b border-cream last:border-0">
                          <div className="flex items-center gap-2">
                            {getDeviceIcon(device)}
                            <span className="text-sm text-ink">{device}</span>
                          </div>
                          <span className="text-sm font-medium text-ink">{count}</span>
                        </div>
                      ))}
                    {Object.keys(analytics.visitors_by_device).length === 0 && (
                      <p className="text-ink-muted text-sm text-center py-4">No data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Top Pages */}
              <Card className="bg-white border-warm">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Eye className="w-5 h-5 text-accent-teal" />
                    Top Pages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="top-pages">
                    {Object.entries(analytics.page_views_by_page)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 6)
                      .map(([page, views]) => (
                        <div key={page} className="flex items-center justify-between py-2 border-b border-cream last:border-0">
                          <span className="text-sm text-ink truncate max-w-[150px]">{page}</span>
                          <span className="text-sm font-medium text-ink">{views}</span>
                        </div>
                      ))}
                    {Object.keys(analytics.page_views_by_page).length === 0 && (
                      <p className="text-ink-muted text-sm text-center py-4">No data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Visitors Table */}
            <Card className="bg-white border-warm">
              <CardHeader>
                <CardTitle className="font-serif text-lg">Recent Visitors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="recent-visitors-table">
                    <thead>
                      <tr className="border-b border-warm">
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Visitor</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Location</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Source</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Device</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Pages</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Time</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent_visitors.map((visitor, i) => (
                        <tr key={i} className="border-b border-cream last:border-0 hover:bg-cream/30">
                          <td className="py-3 px-2 font-mono text-xs text-ink-muted">{visitor.visitor_id}</td>
                          <td className="py-3 px-2">
                            <span className="text-ink">{visitor.country}</span>
                            {visitor.city !== 'Unknown' && (
                              <span className="text-ink-muted">, {visitor.city}</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-ink">{visitor.source}</td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-1 text-ink">
                              {getDeviceIcon(visitor.device)}
                              <span className="hidden sm:inline">{visitor.browser}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-ink">{visitor.pages}</td>
                          <td className="py-3 px-2 text-ink">{formatTime(visitor.time_spent)}</td>
                          <td className="py-3 px-2 text-ink-muted text-xs">
                            {visitor.timestamp ? new Date(visitor.timestamp).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                      {analytics.recent_visitors.length === 0 && (
                        <tr>
                          <td colSpan="7" className="py-8 text-center text-ink-muted">
                            No visitors recorded yet. Analytics will appear once visitors start browsing.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Contact Form Submissions */}
            <Card className="bg-white border-warm mt-8" data-testid="contact-submissions-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-full bg-accent-olive/10 text-accent-olive">
                      <Inbox className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="font-serif text-lg">Contact Form Submissions</CardTitle>
                      <p className="text-xs text-ink-muted mt-0.5">
                        {submissions.length > 0
                          ? `${submissions.length} submission${submissions.length === 1 ? '' : 's'} total`
                          : 'No submissions yet'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchSubmissions}
                      disabled={submissionsLoading}
                      data-testid="submissions-refresh-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${submissionsLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button
                      size="sm"
                      onClick={exportSubmissionsCsv}
                      disabled={!submissions.length}
                      className="bg-accent-olive text-parchment hover:bg-accent-olive-light"
                      data-testid="submissions-export-csv-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="contact-submissions-table">
                    <thead>
                      <tr className="border-b border-warm">
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Received</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Name</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Contact</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Company</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Locale</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Status</th>
                        <th className="text-left py-3 px-2 text-ink-muted font-medium">Goal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((s) => {
                        const isOpen = expandedSubmissionId === s.id;
                        const preview = (s.goal || '').length > 80 ? `${s.goal.slice(0, 80)}…` : s.goal;
                        return (
                          <tr
                            key={s.id}
                            className="border-b border-cream last:border-0 hover:bg-cream/30 cursor-pointer align-top"
                            onClick={() => setExpandedSubmissionId(isOpen ? null : s.id)}
                            data-testid={`contact-submission-row-${s.id}`}
                          >
                            <td className="py-3 px-2 text-ink-muted text-xs whitespace-nowrap">
                              {s.received_at ? new Date(s.received_at).toLocaleString() : '-'}
                            </td>
                            <td className="py-3 px-2 text-ink font-medium">{s.name}</td>
                            <td className="py-3 px-2">
                              <div className="flex flex-col gap-1">
                                <a
                                  href={`mailto:${s.email}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-accent-olive hover:underline inline-flex items-center gap-1"
                                >
                                  <Mail className="w-3 h-3" />
                                  {s.email}
                                </a>
                                {s.phone && (
                                  <a
                                    href={`tel:${s.phone.replace(/\s/g, '')}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-ink-light hover:text-accent-olive text-xs inline-flex items-center gap-1"
                                  >
                                    <span aria-hidden>☎</span>
                                    {s.phone}
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-ink">
                              {s.company ? (
                                <span className="inline-flex items-center gap-1">
                                  <Building2 className="w-3 h-3 text-ink-muted" />
                                  {s.company}
                                </span>
                              ) : (
                                <span className="text-ink-muted">—</span>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <span className="text-xs uppercase tracking-wider text-ink-muted">{s.locale || 'en'}</span>
                            </td>
                            <td className="py-3 px-2">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-amber/15 text-accent-amber capitalize">
                                {s.status || 'new'}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-ink-light max-w-md">
                              {isOpen ? (
                                <span className="block whitespace-pre-wrap leading-relaxed">{s.goal}</span>
                              ) : (
                                <span className="block">{preview}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {!submissions.length && !submissionsLoading && (
                        <tr>
                          <td colSpan="7" className="py-8 text-center text-ink-muted">
                            No contact form submissions yet. They will appear here as visitors send messages.
                          </td>
                        </tr>
                      )}
                      {submissionsLoading && (
                        <tr>
                          <td colSpan="7" className="py-8 text-center text-ink-muted">
                            <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                            Loading submissions…
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {submissions.length > 0 && (
                  <p className="text-xs text-ink-muted mt-3">Tip: click a row to expand the full message.</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
